import { Injectable, inject } from '@angular/core';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';

import { GridViewModel } from '@models/module/grid';
import {
  PdfMarkupAssociation,
  PdfMarkupAssociationDetail,
  PdfMarkupGridItem,
  PdfMarkupKeyValue,
  PdfMarkupModel,
  PdfMarkupTokenResponse,
  PdfMarkupUploadModel,
  PdfMarkupUser,
  PdfMarkupVersion,
} from '@module-instance/pdf-markups/common/pdf-markup.model';
import { HttpService } from '@shared/services';
import { UserStore } from '@signal-store';

@Injectable()
export class PdfMarkupService {
  private http = inject(HttpService);
  private userStore = inject(UserStore);

  getDocumentMarkups(entityInstanceId: number) {
    const params = this.http.buildHttpParams({ entityInstanceId, contractId: this.userStore.contract().id });
    return this.http.get<PdfMarkupModel[]>(`_api/PDFMarkup/GetEntityInstanceLinks`, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getToken(documentId: string, entityInstanceId: number, moduleId: number = SystemModulesEnum.PDFMarkup) {
    const params = this.http.buildHttpParams({
      documentId,
      moduleId,
      entityInstanceId,
    });
    return this.http.get<PdfMarkupTokenResponse>('_api/PDFMarkup/GetToken', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getAssociatedDocuments(documentId: string, entityInstanceId: number) {
    const params = this.http.buildHttpParams({ documentId, entityInstanceId });
    return this.http.get<PdfMarkupAssociation[]>('_api/PDFMarkup/GetAssociatedDocuments', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getAssociationDetails(documentId: string, entityInstanceId: number) {
    const params = this.http.buildHttpParams({ entityInstanceId, documentId });
    return this.http.get<PdfMarkupAssociationDetail[]>('_api/PDFMarkup/AssociationDetails', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getLinkableModules(entityInstanceId: number) {
    const params = this.http.buildHttpParams({ entityInstanceId });
    return this.http.get<PdfMarkupKeyValue[]>('_api/PDFMarkup/GetLinkableModules', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getLinkableDocuments(entityInstanceId: number, moduleTypeId: number, documentId: string) {
    const params = this.http.buildHttpParams({ entityInstanceId, moduleTypeId, documentId });
    return this.http.get<PdfMarkupKeyValue[]>('_api/PDFMarkup/GetLinkableDocuments', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  saveDocumentLink(entityInstanceId: number, documentId: string, moduleTypeInstanceId: number) {
    const params = this.http.buildHttpParams({ entityInstanceId, documentId, moduleTypeInstanceId });
    return this.http.post<PdfMarkupAssociationDetail>('_api/PDFMarkup/SaveDocumentLink', null, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  deleteDocument(fileId: number, entityInstanceId: number) {
    const params = this.http.buildHttpParams({ fileId, entityInstanceId });
    return this.http.put<void>('_api/PDFMarkup/DeleteDocument', null, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  deleteDocumentLink(documentId: string, moduleTypeInstanceId: number, entityInstanceId: number) {
    const params = this.http.buildHttpParams({
      documentId,
      moduleTypeInstanceId,
      entityInstanceId,
    });
    return this.http.delete<void>('_api/PDFMarkup/DeleteDocumentLinkByDocument', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getFlattenedDocument(documentId: string, moduleId: number, entityInstanceId: number) {
    const params = this.http.buildHttpParams({ documentId, moduleId, entityInstanceId });
    return this.http.get<string>('_api/PDFMarkup/FlattenedDocument', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getUserDict(entityInstanceId: number) {
    const params = this.http.buildHttpParams({ entityInstanceId });
    return this.http.get<PdfMarkupUser[]>('_api/PDFMarkup/UserDict', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  saveDocCovers(documentId: string, entityInstanceId: number) {
    const params = this.http.buildHttpParams({ documentId, entityInstanceId });
    return this.http.post<void>('_api/PDFMarkup/SaveDocCovers', null, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getVersions(entityInstanceId: number, fileId: number, isMarkup: boolean) {
    const params = this.http.buildHttpParams({ entityInstanceId, fileId, isMarkup });
    return this.http.get<PdfMarkupVersion[]>('_api/PDFMarkup/GetVersions', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getMimeTypes() {
    return this.http.get<string[]>('_api/PDFMarkup/MimeTypes', { useLoader: false });
  }

  uploadDocument(file: PdfMarkupUploadModel, entityInstanceId: number) {
    const params = this.http.buildHttpParams({ moduleId: SystemModulesEnum.PDFMarkup, entityInstanceId });
    return this.http.postText('_api/PDFMarkup/UploadDocument', file, { httpOptions: { params } });
  }

  uploadDocuments(files: PdfMarkupUploadModel[], entityInstanceId: number) {
    const params = this.http.buildHttpParams({ moduleId: SystemModulesEnum.PDFMarkup, entityInstanceId });
    return this.http.post<number[]>('_api/PDFMarkup/UploadDocuments', files, { httpOptions: { params } });
  }

  getGrid(params: {
    contractId: number;
    entityInstanceId: number;
    moduleId: number;
    page: number;
    pagesize: number;
    criteria?: string;
  }) {
    const httpParams = this.http.buildHttpParams(params);
    return this.http.get<GridViewModel<PdfMarkupGridItem[]>>('_api/PDFMarkup/Grid', {
      httpOptions: { params: httpParams },
      useLoader: false,
    });
  }
}
